import { useCallback, useState } from 'react';
import {
  createFileRoute,
  redirect,
  type SearchSchemaInput,
} from '@tanstack/react-router';
import { BackButton, Card, PageBody, cn, useBelowMobile } from '@wundo/ui';
import type { CurrentUser } from '@wundo/client/auth.ts';
import { PageHeader } from '@/components/PageHeader.tsx';
import { FindingsBar } from '@/components/messages/FindingsBar.tsx';
import {
  MessagesDialogs,
  type MessagesDialog,
} from '@/components/messages/MessagesDialogs.tsx';
import { PersonList } from '@/components/messages/PersonList.tsx';
import { TranscriptHeader } from '@/components/messages/TranscriptHeader.tsx';
import { TranscriptMessages } from '@/components/messages/TranscriptMessages.tsx';
import { TranscriptStatus } from '@/components/messages/TranscriptStatus.tsx';
import { useConversationChoice } from '@/components/messages/useConversationChoice.ts';
import { useConversationInUrl } from '@/components/messages/useConversationInUrl.ts';
import { useFindingNavigation } from '@/components/messages/useFindingNavigation.ts';
import { useMessagesPreferences } from '@/components/messages/useMessagesPreferences.ts';
import { usePeopleOverview } from '@/components/messages/usePeopleOverview.ts';
import { usePersonNavigation } from '@/components/messages/usePersonNavigation.ts';
import { useTranscriptContent } from '@/components/messages/useTranscriptContent.ts';
import { useTranscriptScroll } from '@/components/messages/useTranscriptScroll.ts';
import { useOrganisationFilter } from '@/lib/activeOrganisation.ts';
import { isInternalOrganisation } from '@/lib/staffGate.ts';

/** The filters, as they sit in the URL. The organisation is the header picker's. */
export interface MessagesSearch {
  user?: string;
  conversation?: string;
  /** `1` opens the transcript with the internal messages already showing. */
  internal?: 1;
}

/**
 * The same keys as a link may write them. `?internal=1` was written by hand and
 * by the Vue console as a string, so a link inside the app may say it either way.
 */
export interface MessagesSearchInput {
  user?: string;
  conversation?: string;
  internal?: 1 | '1';
}

function asId(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/*
 * A link out of the Vue console carries `?internal=1`, which parses as the
 * number; one written in this app may carry the string, serialised as `%221%22`.
 * Both are accepted and normalised to the number, so the address bar reads `?internal=1`.
 */
function asInternal(value: unknown): 1 | undefined {
  return value === 1 || value === '1' ? 1 : undefined;
}

/* This page is staff-only tooling, like everything the rail marks internal. */
export const Route = createFileRoute('/_app/messages')({
  staticData: { orgScope: 'optional' },
  validateSearch: (search: MessagesSearchInput & SearchSchemaInput): MessagesSearch => ({
    user: asId(search.user),
    conversation: asId(search.conversation),
    internal: asInternal(search.internal),
  }),
  beforeLoad: ({ context }) => {
    const user = context.queryClient.getQueryData<CurrentUser>(['currentUser']);

    if (!isInternalOrganisation(user)) {
      throw redirect({ to: '/' });
    }
  },
  component: MessagesRoute,
});

function MessagesRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  /* A replace only renames what is already on screen, so the reader stays where they are. */
  const onSearchChange = useCallback(
    (next: MessagesSearch, replace?: boolean) =>
      void navigate({ search: next, replace, resetScroll: !replace }),
    [navigate],
  );

  return <MessagesPage search={search} onSearchChange={onSearchChange} />;
}

export interface MessagesPageProps {
  search: MessagesSearch;
  onSearchChange: (search: MessagesSearch, replace?: boolean) => void;
}

/**
 * Every conversation anybody has had with Wundo, read from the outside: people
 * on the left, one person's transcript on the right, on a phone a screen each.
 * The filters live in the URL, so a conversation worth showing is a link.
 */
export function MessagesPage({ search, onSearchChange }: MessagesPageProps) {
  const userId = search.user ?? null;
  const organisationId = useOrganisationFilter()?.id;
  const belowMobile = useBelowMobile();
  const [dialog, setDialog] = useState<MessagesDialog | null>(null);

  const preferences = useMessagesPreferences(search.internal);
  const { showInternal, groupByChannel } = preferences;

  const { users, selectedUser } = usePeopleOverview(organisationId, userId);

  const { conversations, groups, selection, choose, forget, changeGrouping } =
    useConversationChoice(userId, search.conversation, preferences);

  const { transcript, shown, director, switches, findings, notesByMessage } =
    useTranscriptContent({
      selection,
      conversations: conversations.data,
      showInternal,
      groupByChannel,
    });

  /* Stable while the URL is, so the link is only rewritten when what is read changes. */
  const replaceLinked = useCallback(
    (conversation: string | undefined) =>
      onSearchChange({ ...search, conversation }, true),
    [search, onSearchChange],
  );
  const readingConversation = useConversationInUrl({
    selection,
    merged: transcript.merged,
    groupCount: groups.length,
    linked: search.conversation,
    replaceLinked,
  });

  const { selectUser, backToList } = usePersonNavigation(
    (user) => onSearchChange({ ...search, user, conversation: undefined }),
    forget,
  );

  const scroll = useTranscriptScroll(transcript, shown, belowMobile);
  const { scroller, onScroll } = scroll;
  const { currentFinding, selectFinding } = useFindingNavigation({
    openedKey: selection ? `${userId}:${selection.id}` : null,
    findings,
    notesByMessage,
    transcript,
    scroll,
    belowMobile,
  });

  function openMemories(memoryUserId: string, handles: string[]) {
    setDialog({ kind: 'memories', userId: memoryUserId, handles });
  }

  const transcriptFailed = conversations.isError || transcript.failed;
  const transcriptLoading =
    Boolean(selectedUser) &&
    !transcriptFailed &&
    (conversations.isPending || transcript.loading);

  return (
    <>
      {/* A person outside the list has no header to carry the way back. */}
      {userId && !selectedUser && (
        <BackButton className='mb-2.5 hidden max-mobile:inline-flex' onClick={backToList}>
          All users
        </BackButton>
      )}
      <PageHeader title='Messages' />
      {/* On a phone the page scrolls rather than the body: it is as tall as what it holds. */}
      <PageBody fill className='mt-3.5 max-mobile:mb-0 max-mobile:flex-none'>
        <Card
          padding='none'
          /* Clipped rather than hidden on a phone, so the transcript's header can stick to the top. */
          className='flex h-full min-h-120 overflow-hidden max-mobile:h-auto max-mobile:min-h-0 max-mobile:flex-col max-mobile:overflow-clip'
        >
          <PersonList
            users={users}
            userId={userId}
            organisationId={organisationId}
            onSelect={selectUser}
          />

          <div
            data-ui='transcript'
            className={cn('flex min-w-0 flex-1 flex-col', !userId && 'max-mobile:hidden')}
          >
            {selectedUser && (
              <div className='border-b border-line bg-surface max-mobile:sticky-below-header max-mobile:z-10'>
                <TranscriptHeader
                  person={selectedUser}
                  groups={groups}
                  selection={selection}
                  loading={conversations.isPending}
                  groupByChannel={groupByChannel}
                  onSelectGroup={choose}
                  onGroupingChange={changeGrouping}
                  showInternal={showInternal}
                  onShowInternalChange={preferences.changeShowInternal}
                  canInspect={Boolean(readingConversation)}
                  onInspect={() => setDialog({ kind: 'debug' })}
                  onOpenMemories={() => openMemories(selectedUser.id, [])}
                  onOpenCost={() => setDialog({ kind: 'cost' })}
                  onBack={backToList}
                />
                {!transcriptLoading && !transcriptFailed && (
                  <FindingsBar
                    findings={findings}
                    current={currentFinding}
                    onSelect={selectFinding}
                  />
                )}
              </div>
            )}

            <div
              ref={scroller}
              onScroll={onScroll}
              className='min-h-0 flex-1 overflow-y-auto bg-canvas p-4 max-mobile:flex-none max-mobile:overflow-visible'
            >
              <TranscriptStatus
                userId={userId}
                person={selectedUser}
                peopleLoading={users.isPending}
                failed={transcriptFailed}
                loading={transcriptLoading}
                noConversations={conversations.isSuccess && groups.length === 0}
                noMessages={groups.length > 0 && shown.length === 0}
                onRetry={() =>
                  conversations.isError
                    ? void conversations.refetch()
                    : transcript.retry()
                }
              />

              {shown.length > 0 && !transcriptFailed && (
                <TranscriptMessages
                  messages={shown}
                  loadingMore={transcript.loadingMore}
                  switches={switches}
                  director={director}
                  notesByMessage={notesByMessage}
                  currentFinding={currentFinding}
                  showInternal={showInternal}
                  onOpenMemories={openMemories}
                />
              )}
            </div>
          </div>
        </Card>
      </PageBody>

      <MessagesDialogs
        dialog={dialog}
        onChange={setDialog}
        readingConversation={readingConversation}
        person={selectedUser}
      />
    </>
  );
}
